import ApiGraphQL from './graphql/client'


// Field selection shared by create/update so the client gets back the
// full resource each time. Keeping it on one line is awkward, so we
// indent normally and rely on GraphQL whitespace insensitivity.
const RESOURCE_FIELDS = `
    id companyId type name icon color notes skills
    workSchedule financeInternalCost financeExternalCost
    isDeleted momentCreated
`


const CREATE = `mutation ($input: ResourceCreateInput!) {
    resourceCreate(input: $input) { ${RESOURCE_FIELDS} }
}`

const UPDATE = `mutation ($id: ID!, $input: ResourceUpdateInput!) {
    resourceUpdate(id: $id, input: $input) { ${RESOURCE_FIELDS} }
}`

const DELETE = `mutation ($id: ID!) { resourceDelete(id: $id) }`


// Wrap the underlying deferred so the call site keeps using `.promise()`
// like a normal Request, but rejects on GraphQL `errors` and unwraps
// `data.<mutationName>` on success.
//
// IMPORTANT: ajaxRequestV2 returns a jQuery-style deferred (simply-deferred),
// whose `.then()` predates Promises/A+ — an exception thrown inside a `.then`
// callback is NOT turned into a rejection, it's silently swallowed. On top of
// that, GraphQL always answers HTTP 200 even when the body carries `errors`,
// so the deferred always resolves. We therefore bridge to a native Promise
// and call `reject` explicitly, so `await`/`.catch` at the call sites work.
function _wrap(call: any, dataField: string) {
    return {
        promise: () => new Promise((resolve, reject) => {
            call.promise().then(
                (response: any) => {
                    if (response?.errors?.length) {
                        const message = response.errors.map((e: any) => e.message).join('; ')
                        reject(new Error(message))
                        return
                    }
                    resolve(response?.data?.[dataField])
                },
                // HTTP-level failure (4xx/5xx) — already handled by
                // ajaxRequestV2's autoError, but propagate so the caller's
                // catch still fires.
                (err: any) => reject(err),
            )
        }),
    }
}


export default class ApiResource {

    static create(payload: any) {
        return _wrap(ApiGraphQL.execute(CREATE, {input: payload}), 'resourceCreate')
    }

    static update(id: ID, payload: any) {
        return _wrap(ApiGraphQL.execute(UPDATE, {id, input: payload}), 'resourceUpdate')
    }

    static delete(id: ID) {
        return _wrap(ApiGraphQL.execute(DELETE, {id}), 'resourceDelete')
    }
}
